import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Download, LogIn, Plus, Trash2, Upload } from "lucide-react";
import {
  api,
  ApiError,
  hybridApi,
  type Announcement,
  type DialerScript,
  type Impersonation,
  type Page,
  type Unit,
  type User,
  type UserDetail,
} from "@/lib/api";
import type { Tone } from "@/components/ui";
import { dateTime, endLabel } from "@/lib/format";
import {
  AudioPlayer,
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Dialog,
  Empty,
  ErrorNote,
  Field,
  Input,
  Notice,
  PageHeader,
  Select,
  SortableTh,
  Spinner,
  Table,
  TableFooter,
  Tabs,
  Td,
  Th,
  Toolbar,
} from "@/components/ui";
import { useClientTable, useServerTable } from "@/lib/table";
import { useAuth, useMayUse, useScope } from "@/app/auth";
import { stateInfo } from "./announcements";

const engineLabels: Record<string, string> = {
  a1: "A1",
  robot: "Roboter",
  hybrid: "Hybrid-Roboter",
};

/**
 * The provider a mailbox served by the hybrid robot belongs to: it names the
 * plan the call follows, so it is mandatory for that engine. The list comes
 * from the robot area; a company administrator may not read it and sees the
 * value as it is.
 */
function HybridProviderField({
  value,
  engine,
  onChange,
}: {
  value: string;
  engine: string;
  onChange: (v: string) => void;
}) {
  const mayList = useMayUse("robot");
  const providers = useQuery({
    queryKey: ["hybrid-providers"],
    queryFn: () => hybridApi.providers(),
    enabled: mayList,
  });
  const hint =
    engine === "hybrid"
      ? "Beim Hybrid-Roboter zwingend: der Provider bestimmt den Ablaufplan."
      : "Nur für den Hybrid-Roboter relevant.";
  if (!mayList) {
    return (
      <Field label="Provider (Hybrid-Roboter)" hint={hint}>
        <Input value={value || "—"} disabled readOnly />
      </Field>
    );
  }
  return (
    <Field label="Provider (Hybrid-Roboter)" hint={hint}>
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {(providers.data ?? []).map((p) => (
          <option key={p.key} value={p.key}>
            {p.displayName} ({p.key})
          </option>
        ))}
      </Select>
    </Field>
  );
}

// --- list ----------------------------------------------------------------

/** How a user's second factor is named in the interface. */
const MFA_LABELS: Record<string, string> = {
  off: "nicht eingerichtet",
  sms: "SMS",
  totp: "Authenticator-App",
};

export function UsersPage() {
  const scope = useScope();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [ticketUrl, setTicketUrl] = useState<string | null>(null);

  // Only a global administrator may open somebody else's portal, so nobody
  // else is shown a column of controls that can only ever refuse. The server
  // enforces it regardless; this is about not drawing dead buttons on a
  // thousand rows.
  const { me } = useAuth();
  const mayImpersonate = me?.role === "global_admin";

  // A company can have thousands of people, so the server pages and orders
  // them: sorting the five hundred that happened to be fetched would put the
  // wrong names on the first screen.
  const [total, setTotal] = useState(0);
  const table = useServerTable({
    total,
    initial: { key: "lastName", dir: "asc" },
    resetOn: [scope, search, role, activeOnly],
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["users", scope, search, role, activeOnly, table.query],
    queryFn: () =>
      api<Page<User>>(
        `/api/admin/users?${table.query}&q=${encodeURIComponent(search)}` +
          `&role=${role}&activeOnly=${activeOnly}`,
      ),
  });

  useEffect(() => {
    if (data) setTotal(data.total);
  }, [data]);

  const bulk = useMutation({
    mutationFn: (vars: { field: string; value: string }) =>
      api<{ changed: number }>("/api/admin/users/bulk", {
        method: "POST",
        body: {
          companyId: scope,
          userIds: Array.from(selected),
          field: vars.field,
          value: vars.value,
        },
      }),
    onSuccess: () => {
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  // The tab is opened by the click handler, before this ever runs, and is
  // carried through the mutation so that success can send it somewhere and
  // failure can close it again. See the comment on the button.
  const impersonate = useMutation({
    mutationFn: (vars: { id: number; tab: Window | null }) =>
      api<Impersonation>(`/api/admin/users/${vars.id}/impersonate`, { method: "POST" }),
    onSuccess: (ticket, vars) => {
      // replace() rather than href=, so the placeholder is not a step the
      // reader can go back to.
      if (vars.tab) vars.tab.location.replace(ticket.url);
      // The browser refused the window outright. The link is still good for
      // most of a minute, so it is offered as something to click.
      else setTicketUrl(ticket.url);
    },
    onError: (_error, vars) => vars.tab?.close(),
  });

  const users = data?.items ?? [];
  // "all" means the page in front of the reader, not every row behind it: a
  // bulk change should never reach further than what was seen.
  const allSelected =
    users.length > 0 && users.every((u) => selected.has(u.id));

  return (
    <>
      <PageHeader
        title="Benutzer"
        description="Die Menschen, deren Mobilbox dieses System bespielt."
        actions={
          <>
            <Button onClick={() => setImporting(true)}>
              <Upload className="size-4" aria-hidden /> Import
            </Button>
            <Button
              onClick={() => {
                window.location.href = `/api/admin/companies/${scope}/users/export`;
              }}
              disabled={!scope}
              title={!scope ? "Zuerst eine Firma wählen" : undefined}
            >
              <Download className="size-4" aria-hidden /> Export
            </Button>
            <Button variant="primary" onClick={() => setCreating(true)} disabled={!scope}>
              <Plus className="size-4" aria-hidden /> Benutzer anlegen
            </Button>
          </>
        }
      />

      <Toolbar>
        <Input
          className="w-64"
          placeholder="Name, E-Mail oder Nummer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select className="w-48" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">alle Rollen</option>
          <option value="employee">Mitarbeiter</option>
          <option value="secretariat">Sekretariat</option>
          <option value="company_admin">Firmenadmin</option>
          <option value="self_admin">Mehrfirmen-Admin</option>
          <option value="global_admin">Globaler Admin</option>
        </Select>
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          nur aktive
        </label>
      </Toolbar>

      {selected.size > 0 && (
        <div className="mb-3">
          <Card className="flex flex-wrap items-center gap-2 p-3">
            <span className="text-sm font-medium">{selected.size} ausgewählt</span>
            <div className="flex-1" />
            <Button size="sm" onClick={() => bulk.mutate({ field: "active", value: "true" })}>
              aktivieren
            </Button>
            <Button size="sm" onClick={() => bulk.mutate({ field: "active", value: "false" })}>
              deaktivieren
            </Button>
            <Button size="sm" onClick={() => bulk.mutate({ field: "billable", value: "true" })}>
              abrechenbar
            </Button>
            <Button
              size="sm"
              onClick={() => bulk.mutate({ field: "billable", value: "false" })}
            >
              nicht abrechenbar
            </Button>
            <Button
              size="sm"
              onClick={() => bulk.mutate({ field: "delivery_engine", value: "a1" })}
            >
              auf A1
            </Button>
            <Button
              size="sm"
              onClick={() => bulk.mutate({ field: "delivery_engine", value: "robot" })}
            >
              auf Roboter
            </Button>
            <Button
              size="sm"
              onClick={() => bulk.mutate({ field: "delivery_engine", value: "hybrid" })}
            >
              auf Hybrid-Roboter
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Auswahl aufheben
            </Button>
          </Card>
        </div>
      )}

      {bulk.error && <ErrorNote>{(bulk.error as Error).message}</ErrorNote>}
      {impersonate.error && <ErrorNote>{impersonateError(impersonate.error)}</ErrorNote>}
      {error && <ErrorNote>{(error as Error).message}</ErrorNote>}

      {isLoading ? (
        <Spinner />
      ) : (
        <Card>
          <Table dense>
            <thead>
              <tr>
                <Th className="w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => {
                      const next = new Set(selected);
                      for (const u of users) {
                        if (e.target.checked) next.add(u.id);
                        else next.delete(u.id);
                      }
                      setSelected(next);
                    }}
                    aria-label="alle auf dieser Seite auswählen"
                  />
                </Th>
                <SortableTh table={table} sortKey="lastName">
                  Name
                </SortableTh>
                <SortableTh table={table} sortKey="msisdn">
                  Nummer
                </SortableTh>
                <SortableTh table={table} sortKey="engine">
                  Weg
                </SortableTh>
                <SortableTh table={table} sortKey="role">
                  Rolle
                </SortableTh>
                <SortableTh table={table} sortKey="mailbox" first="desc">
                  Mobilbox
                </SortableTh>
                <SortableTh table={table} sortKey="status" first="desc">
                  Zustand
                </SortableTh>
                <SortableTh table={table} sortKey="banCheck">
                  BAN
                </SortableTh>
                <SortableTh table={table} sortKey="recording">
                  Dateien
                </SortableTh>
                {mayImpersonate && <Th className="w-10" />}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <Td>
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) next.add(u.id);
                        else next.delete(u.id);
                        setSelected(next);
                      }}
                      aria-label={`${u.lastName} auswählen`}
                    />
                  </Td>
                  <Td>
                    <div className="flex max-w-48 items-center gap-1.5">
                      <button
                        className="min-w-0 text-left font-medium hover:underline"
                        onClick={() => navigate(`/users/${u.id}`)}
                      >
                        {fullName(u)}
                      </button>
                      {!u.active && <Badge>inaktiv</Badge>}
                    </div>
                    <div className="max-w-48 truncate text-xs muted" title={u.email}>
                      {u.email}
                    </div>
                  </Td>
                  <Td className="tabular-nums">{u.msisdn || "—"}</Td>
                  <Td>{engineLabels[u.engine] ?? u.engine}</Td>
                  <Td className="whitespace-nowrap">{roleLabel(u.role)}</Td>
                  <Td>
                    <StatusBadge {...mailboxInfo(u)} />
                  </Td>
                  <Td>
                    <StatusBadge {...healthInfo(u)} />
                  </Td>
                  <Td>
                    <StatusBadge {...banInfo(u)} />
                  </Td>
                  <Td>
                    <StatusBadge {...filesInfo(u)} />
                  </Td>
                  {mayImpersonate && (
                    <Td className="text-right">
                      {/* The title sits on the span and not on the button:
                          Button carries disabled:pointer-events-none, so a
                          disabled one never shows a tooltip of its own. */}
                      <span title={portalReason(u) ?? "Portal im Namen dieser Person öffnen"}>
                        <Button
                          size="sm"
                          variant="ghost"
                          // The ordinary ghost hover is a faint grey wash,
                          // which on a row of status badges reads as nothing
                          // happening. This one is the brand colour, because
                          // the control does something a good deal more
                          // consequential than the rest of the row and should
                          // look like it under the pointer.
                          className="hover:bg-brand-600 hover:text-white focus-visible:bg-brand-600 focus-visible:text-white"
                          aria-label={`Portal als ${fullName(u)} öffnen`}
                          disabled={
                            portalReason(u) !== undefined ||
                            (impersonate.isPending && impersonate.variables?.id === u.id)
                          }
                          onClick={() => {
                            // Opened here, inside the click itself. A
                            // window.open() that waits for the request is no
                            // longer part of the user gesture, and Safari and
                            // Firefox refuse it — which is why this must not
                            // be "tidied" behind the await later on.
                            const tab = window.open("", "_blank");
                            if (tab) {
                              tab.document.write(PLACEHOLDER);
                              tab.document.close();
                            }
                            impersonate.mutate({ id: u.id, tab });
                          }}
                        >
                          <LogIn className="size-4" aria-hidden />
                        </Button>
                      </span>
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
          {users.length === 0 && (
            <Empty
              title="Keine Benutzer"
              hint={scope ? undefined : "Zuerst oben eine Firma wählen."}
            />
          )}
          <TableFooter table={table} noun="Benutzer" />
        </Card>
      )}

      {ticketUrl && (
        <Dialog open onClose={() => setTicketUrl(null)} title="Portal öffnen">
          <p className="text-sm">
            Der Browser hat das neue Fenster blockiert. Der Link gilt etwa eine Minute und
            kann nur einmal verwendet werden.
          </p>
          <div className="mt-5 flex justify-end">
            {/* An anchor and not a Button: the reader's click on it is a fresh
                gesture, which no pop-up blocker stands in the way of, and a
                <button> cannot carry target="_blank" anyway. */}
            <a
              href={ticketUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setTicketUrl(null)}
              className="inline-flex h-8 shrink-0 items-center justify-center rounded-md bg-brand-600 px-3 text-sm font-medium whitespace-nowrap text-white shadow-xs hover:bg-brand-700"
            >
              Portal öffnen
            </a>
          </div>
        </Dialog>
      )}

      {importing && <ImportDialog companyId={scope} onClose={() => setImporting(false)} />}
      {creating && (
        <UserDialog
          companyId={scope}
          onClose={() => setCreating(false)}
          onSaved={(u) => {
            setCreating(false);
            navigate(`/users/${u.id}`);
          }}
        />
      )}
    </>
  );
}

// --- detail --------------------------------------------------------------

export function UserDetailPage() {
  const { id } = useParams();
  const userId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("stammdaten");
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => api<UserDetail>(`/api/admin/users/${userId}/detail`),
    enabled: Number.isFinite(userId),
  });

  const remove = useMutation({
    mutationFn: () => api<void>(`/api/admin/users/${userId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      navigate("/users");
    },
  });

  const standard = useMutation({
    mutationFn: () =>
      api<{ status: string }>(`/api/admin/users/${userId}/default-greeting`, {
        method: "POST",
      }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;
  if (!data) return null;

  return (
    <>
      <PageHeader
        title={`${data.lastName}, ${data.firstName}`}
        description={data.email}
        actions={
          <>
            <Button onClick={() => standard.mutate()}>Standardansage einspielen</Button>
            <Button variant="danger" onClick={() => setDeleting(true)}>
              <Trash2 className="size-4" aria-hidden /> Löschen
            </Button>
          </>
        }
      />

      {standard.isSuccess && (
        <div className="mb-4">
          <Notice tone="success">
            Die Standardansage wurde eingereiht und wird vom Worker eingespielt.
          </Notice>
        </div>
      )}
      {data.banError && (
        <div className="mb-4">
          <Notice tone="warning">
            Die Prüfung beim Provider schlug fehl: {data.banError}
          </Notice>
        </div>
      )}

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: "stammdaten", label: "Stammdaten" },
          { id: "ansagen", label: "Ansagen" },
        ]}
      />

      {tab === "stammdaten" && <UserForm user={data} />}
      {tab === "ansagen" && <UserAnnouncements userId={userId} />}

      <ConfirmDialog
        open={deleting}
        title="Benutzer löschen"
        message={
          <>
            <strong>
              {data.firstName} {data.lastName}
            </strong>{" "}
            wird gelöscht, samt geplanten Ansagen. Im Identity-Provider und in
            der Central-API wird das Konto ebenfalls entfernt, sofern die
            Synchronisation der Firma aktiv ist.
          </>
        }
        onConfirm={() => remove.mutate()}
        onClose={() => setDeleting(false)}
        busy={remove.isPending}
      />
    </>
  );
}

function UserAnnouncements({ userId }: { userId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["user", userId, "announcements"],
    queryFn: () => api<Announcement[]>(`/api/admin/users/${userId}/announcements`),
  });

  const items = data ?? [];
  // One person's absences: the whole list is here, so it sorts in the browser.
  const table = useClientTable(items, {
    initial: { key: "startsAt", dir: "desc" },
    pageSize: 10,
    accessors: {
      // An open end sorts after every dated one, which is where "bis auf
      // Weiteres" belongs when the list is read by end date.
      endsAt: (a) => a.endsAt ?? "",
      state: (a) => stateInfo[a.state].label,
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <SortableTh table={table} sortKey="startsAt" first="desc">
              Von
            </SortableTh>
            <SortableTh table={table} sortKey="endsAt" first="desc">
              Bis
            </SortableTh>
            <SortableTh table={table} sortKey="state">
              Zustand
            </SortableTh>
            <SortableTh table={table} sortKey="displayName">
              Bezeichnung
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {table.rows.map((a) => (
            <tr key={a.id}>
              <Td>{dateTime(a.startsAt)}</Td>
              <Td>{endLabel(a.endsAt)}</Td>
              <Td>
                <Badge tone={stateInfo[a.state].tone}>{stateInfo[a.state].label}</Badge>
              </Td>
              <Td className="muted">{a.displayName || "—"}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {items.length === 0 && <Empty title="Keine geplanten oder laufenden Ansagen" />}
      <TableFooter table={table} noun="Ansagen" />
    </Card>
  );
}

// --- the editing form ----------------------------------------------------

function UserForm({ user }: { user: UserDetail }) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [draft, setDraft] = useState<UserDetail>({ ...user, password: "", mailboxPin: "" });
  const [resetting, setResetting] = useState(false);
  const set = <K extends keyof UserDetail>(k: K, v: UserDetail[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const departments = useQuery({
    queryKey: ["company", user.companyId, "departments"],
    queryFn: () => api<Unit[]>(`/api/admin/companies/${user.companyId}/departments`),
  });
  const positions = useQuery({
    queryKey: ["company", user.companyId, "positions"],
    queryFn: () => api<Unit[]>(`/api/admin/companies/${user.companyId}/positions`),
  });
  const scripts = useQuery({
    queryKey: ["dialer-scripts"],
    queryFn: () => api<DialerScript[]>("/api/admin/dialer-scripts"),
  });
  // Die beiden Pools, aus denen das Portal die Vertretungen anbietet: aktive
  // Mitarbeiter der Firma und deren Sekretariate. Dieselbe Auswahl wie in der
  // alten Verwaltung, die /admin/user/representations zweimal geholt hat.
  const useStandIns = (role: "employee" | "secretariat") =>
    useQuery({
      queryKey: ["company", user.companyId, "stand-ins", role],
      queryFn: () =>
        api<Page<User>>(
          `/api/admin/users?companyId=${user.companyId}&role=${role}&activeOnly=true` +
            `&sort=lastName&limit=1000`,
        ),
    });
  const colleagues = useStandIns("employee");
  const offices = useStandIns("secretariat");

  // Der Weg für das verlorene Telefon. Nur ein globaler Administrator, und
  // nicht für sich selbst: den eigenen Faktor aus einer Sitzung heraus
  // zurückzusetzen, die dieser Faktor aufgemacht hat, hebt ihn auf.
  const resetMfa = useMutation({
    mutationFn: () => api<void>(`/api/admin/users/${user.id}/mfa`, { method: "DELETE" }),
    onSuccess: () => {
      setResetting(false);
      void qc.invalidateQueries({ queryKey: ["user", user.id] });
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api<UserDetail>(`/api/admin/users/${user.id}`, { method: "PUT", body: draft }),
    onSuccess: (u) => {
      qc.setQueryData(["user", user.id], u);
      void qc.invalidateQueries({ queryKey: ["users"] });
      setDraft({ ...u, password: "", mailboxPin: "" });
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Card className="p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Vorname">
            <Input value={draft.firstName} onChange={(e) => set("firstName", e.target.value)} />
          </Field>
          <Field label="Nachname">
            <Input value={draft.lastName} onChange={(e) => set("lastName", e.target.value)} />
          </Field>
          <Field label="E-Mail">
            <Input
              type="email"
              value={draft.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </Field>
          <Field label="Geschlecht" hint="Bestimmt, welche generische Aufnahme greift.">
            <Select value={draft.gender} onChange={(e) => set("gender", e.target.value)}>
              <option value="">unbekannt</option>
              <option value="male">männlich</option>
              <option value="female">weiblich</option>
            </Select>
          </Field>
          <Field label="Mobilnummer" hint="Wird normalisiert: 436641234567.">
            <Input value={draft.msisdn} onChange={(e) => set("msisdn", e.target.value)} />
          </Field>
          <SpokenNameField
            companyId={draft.companyId}
            value={draft.spokenName}
            fallback={`${draft.firstName} ${draft.lastName}`.trim()}
            onChange={(v) => set("spokenName", v)}
          />
          <Field label="VPN / Kurzwahl" hint="Steht in Benachrichtigungen vor der Mobilnummer.">
            <Input value={draft.vpnNumber} onChange={(e) => set("vpnNumber", e.target.value)} />
          </Field>
          <Field label="Personalnummer">
            <Input
              value={draft.personnelNo}
              onChange={(e) => set("personnelNo", e.target.value)}
            />
          </Field>
          <Field label="BAN" hint="Leer: der BAN der Firma gilt.">
            <Input
              value={draft.billingAccount}
              onChange={(e) => set("billingAccount", e.target.value)}
            />
          </Field>

          <Field label="Rolle">
            <Select value={draft.role} onChange={(e) => set("role", e.target.value)}>
              {grantableRoles(me?.role, draft.role).map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Zwei-Faktor"
            hint={
              draft.mfa === "off"
                ? isAdminRole(draft.role)
                  ? "Wird beim nächsten Anmelden eingerichtet — verpflichtend für jede Verwaltungsrolle."
                  : "Nur für Verwaltungsrollen."
                : "Von der betroffenen Person selbst eingerichtet."
            }
          >
            {/* Not a form field: which second factor somebody uses is set when
                they set it up, not by whoever is editing their record. The one
                thing somebody else may do is take it away, so that a lost
                phone is a support call and not a lost account. */}
            <div className="flex h-9 items-center gap-2">
              <Badge tone={draft.mfa === "off" ? "warning" : "success"}>
                {MFA_LABELS[draft.mfa] ?? draft.mfa}
              </Badge>
              {user.passkeys > 0 && (
                <Badge tone="info">
                  {user.passkeys === 1 ? "1 Passkey" : `${user.passkeys} Passkeys`}
                </Badge>
              )}
              {draft.mfa !== "off" &&
                me?.role === "global_admin" &&
                me.userId !== user.id && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setResetting(true)}
                  >
                    zurücksetzen
                  </Button>
                )}
            </div>
          </Field>

          <Field label="Einspielweg">
            <Select
              value={draft.deliveryEngine}
              onChange={(e) =>
                set("deliveryEngine", e.target.value as UserDetail["deliveryEngine"])
              }
            >
              <option value="a1">A1-Schnittstelle</option>
              <option value="robot">Roboter</option>
              <option value="hybrid">Hybrid-Roboter</option>
            </Select>
          </Field>
          <HybridProviderField
            value={draft.hybridProvider ?? ""}
            engine={draft.deliveryEngine}
            onChange={(v) => set("hybridProvider", v)}
          />
          <Field
            label="Dialer-Skript"
            hint={
              draft.deliveryEngine === "robot"
                ? "Beim Roboter zwingend: ohne Skript hat der Anruf nichts zu tun."
                : "Nur für den Roboter relevant, nicht für den Hybrid-Roboter."
            }
          >
            <Select
              value={draft.dialerScriptId}
              onChange={(e) => set("dialerScriptId", Number(e.target.value))}
            >
              <option value={0}>—</option>
              {(scripts.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Abteilung">
            <Select
              value={draft.departmentId}
              onChange={(e) => {
                const id = Number(e.target.value);
                set("departmentId", id);
                set("departmentIds", id ? [id] : []);
              }}
            >
              <option value={0}>—</option>
              {(departments.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Funktion">
            <Select
              value={draft.positionId}
              onChange={(e) => set("positionId", Number(e.target.value))}
            >
              <option value={0}>—</option>
              {(positions.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Standard Vertretung"
            hint="Ist in Ansagen mit Vertretung vorausgewählt; die Person kann sie im Portal selbst ändern."
          >
            <Select
              value={draft.favouriteProxyId}
              onChange={(e) => set("favouriteProxyId", Number(e.target.value))}
            >
              <option value={0}>—</option>
              {(colleagues.data?.items ?? [])
                .filter((c) => c.id !== user.id)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </option>
                ))}
            </Select>
          </Field>
          <Field
            label="Office Vertretung"
            hint="Vorauswahl für Ansagen, die an ein Sekretariat verweisen."
          >
            <Select
              value={draft.favouriteSecretariatId}
              onChange={(e) => set("favouriteSecretariatId", Number(e.target.value))}
            >
              <option value={0}>—</option>
              {(offices.data?.items ?? [])
                .filter((c) => c.id !== user.id)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </option>
                ))}
            </Select>
          </Field>

          <Field
            label="Neues Passwort"
            hint="Leer lassen, um es nicht zu ändern. Ein importierter SHA-1-Hash wird damit endgültig ersetzt."
          >
            <Input
              type="password"
              autoComplete="new-password"
              value={draft.password ?? ""}
              onChange={(e) => set("password", e.target.value)}
            />
          </Field>
          <Field
            label="Mobilbox-PIN"
            hint={
              draft.hasPin
                ? 'Hinterlegt und verschlüsselt. Neu eingeben zum Ersetzen, "-" zum Entfernen.'
                : "Roboter und Hybrid-Roboter können sich ohne PIN nicht anmelden."
            }
          >
            <Input
              type="password"
              autoComplete="off"
              value={draft.mailboxPin ?? ""}
              onChange={(e) => set("mailboxPin", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-4">
          <Checkbox
            label="Aktiv"
            checked={draft.active}
            onChange={(e) => set("active", e.target.checked)}
          />
          <Checkbox
            label="Abrechenbar"
            hint="Nur abrechenbare Benutzer erscheinen im Monatslauf."
            checked={draft.billable}
            onChange={(e) => set("billable", e.target.checked)}
          />
          <Checkbox
            label="Demo-Konto"
            hint="Demo-Konten werden vom Worker nicht eingespielt."
            checked={draft.demo}
            onChange={(e) => set("demo", e.target.checked)}
          />
        </div>

        {save.error && (
          <div className="mt-4">
            <ErrorNote>{(save.error as Error).message}</ErrorNote>
          </div>
        )}

        <div className="mt-5 flex items-center gap-3">
          <Button type="submit" variant="primary" disabled={save.isPending}>
            Speichern
          </Button>
          {save.isSuccess && <span className="text-xs muted">Gespeichert</span>}
        </div>
      </Card>

      <ConfirmDialog
        open={resetting}
        title="Zweiten Faktor zurücksetzen"
        message={
          <>
            <strong>
              {user.firstName} {user.lastName}
            </strong>{" "}
            kann sich danach wieder allein mit dem Passwort anmelden und richtet
            beim nächsten Mal einen neuen Faktor ein. Registrierte Passkeys
            werden mit entfernt. Alle offenen Sitzungen dieser Person werden
            beendet. Der Vorgang steht mit Ihrem Namen im Änderungsprotokoll.
          </>
        }
        confirmLabel="Zurücksetzen"
        error={resetMfa.error ? (resetMfa.error as Error).message : null}
        onConfirm={() => resetMfa.mutate()}
        onClose={() => setResetting(false)}
        busy={resetMfa.isPending}
      />
    </form>
  );
}

/** Welche Rollen in die Verwaltung dürfen — und damit einen Faktor brauchen. */
function isAdminRole(role: string): boolean {
  return role === "global_admin" || role === "self_admin" || role === "company_admin";
}

const roleOrder = ["employee", "secretariat", "company_admin", "self_admin", "global_admin"];

/** Rang einer Rolle; die Portal-Rollen teilen sich den untersten. */
function roleRank(role: string | undefined): number {
  switch (role) {
    case "global_admin":
      return 3;
    case "self_admin":
      return 2;
    case "company_admin":
      return 1;
    case "employee":
    case "secretariat":
      return 0;
    default:
      return -1;
  }
}

/**
 * Die Rollen, die man vergeben darf: die eigene und alles darunter. Der Server
 * prüft dasselbe; hier geht es nur darum, nichts anzubieten, was er ablehnt.
 * Die aktuelle Rolle bleibt sichtbar, damit das Feld nicht leer wirkt.
 */
function grantableRoles(own: string | undefined, current: string): string[] {
  return roleOrder.filter((r) => roleRank(r) <= roleRank(own) || r === current);
}

// --- creating ------------------------------------------------------------

function UserDialog({
  companyId,
  onClose,
  onSaved,
}: {
  companyId: number;
  onClose: () => void;
  onSaved: (u: UserDetail) => void;
}) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [draft, setDraft] = useState({
    companyId,
    email: "",
    firstName: "",
    lastName: "",
    gender: "",
    role: "employee",
    deliveryEngine: "a1",
    hybridProvider: "",
    active: true,
    password: "",
  });
  const set = (k: string, v: unknown) => setDraft((d) => ({ ...d, [k]: v }));

  const create = useMutation({
    mutationFn: () =>
      api<UserDetail>("/api/admin/users", { method: "POST", body: draft }),
    onSuccess: (u) => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      onSaved(u);
    },
  });

  return (
    <Dialog open onClose={onClose} title="Benutzer anlegen">
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Vorname">
            <Input value={draft.firstName} onChange={(e) => set("firstName", e.target.value)} />
          </Field>
          <Field label="Nachname">
            <Input value={draft.lastName} onChange={(e) => set("lastName", e.target.value)} />
          </Field>
        </div>
        <Field label="E-Mail">
          <Input
            type="email"
            value={draft.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Rolle">
            <Select value={draft.role} onChange={(e) => set("role", e.target.value)}>
              {grantableRoles(me?.role, draft.role)
                .filter((r) => r !== "global_admin")
                .map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Einspielweg">
            <Select
              value={draft.deliveryEngine}
              onChange={(e) => set("deliveryEngine", e.target.value)}
            >
              <option value="a1">A1-Schnittstelle</option>
              <option value="robot">Roboter</option>
              <option value="hybrid">Hybrid-Roboter</option>
            </Select>
          </Field>
        </div>
        {draft.deliveryEngine === "hybrid" && (
          <HybridProviderField
            value={draft.hybridProvider}
            engine={draft.deliveryEngine}
            onChange={(v) => set("hybridProvider", v)}
          />
        )}
        <Field
          label="Passwort"
          hint="Nur nötig für Konten, die sich anmelden. Mitarbeiter brauchen keines."
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={draft.password}
            onChange={(e) => set("password", e.target.value)}
          />
        </Field>

        {create.error && <ErrorNote>{(create.error as Error).message}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" disabled={create.isPending} onClick={() => create.mutate()}>
            Anlegen
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// --- import --------------------------------------------------------------

type ImportPreview = {
  filename: string;
  rows: number;
  preview: string[][];
  mapping: Record<string, string>;
  fields: { field: string; label: string }[];
};

type ImportReport = {
  rows: number;
  created: number;
  updated: number;
  skipped: number;
  problems: string[];
  preview: { email: string; firstName: string; lastName: string }[];
  dryRun: boolean;
};

/**
 * The import in two steps: read the file and show what the columns were
 * understood as, then write. The legacy importer had only the second step, so
 * a shifted column produced a company full of people named after phone
 * numbers, with no report saying so.
 */
function ImportDialog({ companyId, onClose }: { companyId: number; onClose: () => void }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mapping, setMapping] = useState<Record<number, string>>({});
  const [hasHeader, setHasHeader] = useState(true);
  const [update, setUpdate] = useState(false);
  const [createUnits, setCreateUnits] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);

  const post = async (path: string, extra: Record<string, string>) => {
    if (!file) throw new Error("keine Datei gewählt");
    // The options go first: a multipart body arrives in the order it was
    // built, and the server reads the file as a stream.
    const form = new FormData();
    for (const [k, v] of Object.entries(extra)) form.append(k, v);
    form.append("file", file, file.name);

    const res = await fetch(path, {
      method: "POST",
      body: form,
      credentials: "same-origin",
      headers: { "X-CSRF-Token": csrf() },
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const read = useMutation({
    mutationFn: () =>
      post(`/api/admin/companies/${companyId}/users/import/preview`, {}) as Promise<ImportPreview>,
    onSuccess: (p) => {
      setPreview(p);
      const m: Record<number, string> = {};
      for (const [k, v] of Object.entries(p.mapping)) m[Number(k)] = v;
      setMapping(m);
    },
  });

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      post(`/api/admin/companies/${companyId}/users/import`, {
        hasHeader: String(hasHeader),
        update: String(update),
        createUnits: String(createUnits),
        dryRun: String(dryRun),
        mapping: Object.entries(mapping)
          .filter(([, f]) => f)
          .map(([i, f]) => `${i}=${f}`)
          .join(","),
      }) as Promise<ImportReport>,
    onSuccess: (r) => {
      setReport(r);
      if (!r.dryRun) void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const columns = preview?.preview[0]?.length ?? 0;

  return (
    <Dialog open onClose={onClose} title="Benutzer importieren">
      <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto">
        <Notice>
          CSV oder XLSX. Die erste Zeile wird als Überschrift gelesen und die
          Spalten werden daraus erraten — bitte prüfen, bevor geschrieben wird.
        </Notice>

        <div className="flex items-center gap-2">
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.xlsx,text/csv"
            hidden
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setPreview(null);
              setReport(null);
            }}
          />
          <Button onClick={() => fileInput.current?.click()}>Datei wählen</Button>
          <span className="text-sm muted">{file?.name ?? "keine Datei gewählt"}</span>
          <div className="flex-1" />
          <Button
            variant="primary"
            disabled={!file || read.isPending}
            onClick={() => read.mutate()}
          >
            Einlesen
          </Button>
        </div>

        {read.error && <ErrorNote>{(read.error as Error).message}</ErrorNote>}

        {preview && (
          <>
            <p className="text-sm">
              {preview.rows} Zeilen gelesen. Zuordnung der Spalten:
            </p>

            <div className="overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    {Array.from({ length: columns }, (_, i) => (
                      <Th key={i}>
                        <Select
                          className="min-w-36"
                          value={mapping[i] ?? ""}
                          onChange={(e) =>
                            setMapping({ ...mapping, [i]: e.target.value })
                          }
                        >
                          <option value="">— ignorieren —</option>
                          {preview.fields.map((f) => (
                            <option key={f.field} value={f.field}>
                              {f.label}
                            </option>
                          ))}
                        </Select>
                      </Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.slice(0, 6).map((row, i) => (
                    <tr key={i}>
                      {Array.from({ length: columns }, (_, j) => (
                        <Td key={j} className="whitespace-nowrap text-xs">
                          {row[j] ?? ""}
                        </Td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div>
              <Checkbox
                label="Erste Zeile ist eine Überschrift"
                checked={hasHeader}
                onChange={(e) => setHasHeader(e.target.checked)}
              />
              <Checkbox
                label="Bestehende Benutzer aktualisieren"
                hint="Ohne diesen Haken werden bekannte E-Mail-Adressen gemeldet und übersprungen."
                checked={update}
                onChange={(e) => setUpdate(e.target.checked)}
              />
              <Checkbox
                label="Abteilungen und Funktionen anlegen, wenn sie fehlen"
                checked={createUnits}
                onChange={(e) => setCreateUnits(e.target.checked)}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button disabled={run.isPending} onClick={() => run.mutate(true)}>
                Probelauf
              </Button>
              <Button
                variant="primary"
                disabled={run.isPending}
                onClick={() => run.mutate(false)}
              >
                Importieren
              </Button>
            </div>
          </>
        )}

        {run.error && <ErrorNote>{(run.error as Error).message}</ErrorNote>}

        {report && (
          <Card className="p-4">
            <p className="font-medium">
              {report.dryRun ? "Probelauf" : "Import abgeschlossen"}
            </p>
            <p className="mt-1 text-sm">
              {report.rows} Zeilen · {report.created} neu · {report.updated}{" "}
              aktualisiert · {report.skipped} übersprungen
            </p>
            {report.problems.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium">Nicht übernommen:</p>
                <ul className="mt-1 max-h-48 list-inside list-disc overflow-y-auto text-sm muted">
                  {report.problems.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        )}
      </div>
    </Dialog>
  );
}

function csrf(): string {
  const match = document.cookie.match(/(?:^|;\s*)mbm_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

// --- the state columns ---------------------------------------------------
//
// Four questions the legacy overview answered next to each name and this one
// did not: what is on the box, is anything wrong, does A1 accept the number,
// and are the recordings there. Each comes back as a label, a tone and a
// sentence that explains it on hover, so the columns stay readable at a
// glance and still say why.

type Status = { label: string; tone: Tone; hint?: string };

function StatusBadge({ label, tone, hint }: Status) {
  return (
    <span title={hint}>
      <Badge tone={tone}>{label}</Badge>
    </span>
  );
}

/** What is on the mailbox right now. */
function mailboxInfo(u: User): Status {
  switch (u.mailbox) {
    case "vacation":
      return {
        label: "Abwesenheit",
        tone: "info",
        hint: [u.absenceName, `bis ${endLabel(u.absenceUntil)}`]
          .filter(Boolean)
          .join(" — "),
      };
    case "default":
      return {
        label: "Standard",
        tone: "success",
        hint: "Die Standardansage der Firma ist eingespielt.",
      };
    case "unconfigured":
      return {
        label: "leer",
        tone: "neutral",
        hint: "Auf dieser Mobilbox hat das System noch nichts hinterlegt.",
      };
    default:
      // A deployment without the master data service serves the plain
      // record; then there is nothing to say rather than something wrong.
      return { label: "—", tone: "neutral" };
  }
}

/** Whether anything about this person is in error. */
function healthInfo(u: User): Status {
  if (u.mailbox === undefined) return { label: "—", tone: "neutral" };
  if (u.lastError) {
    return {
      label: "Fehler",
      tone: "danger",
      hint: u.lastErrorAt ? `${u.lastError} (${dateTime(u.lastErrorAt)})` : u.lastError,
    };
  }
  return { label: "OK", tone: "success" };
}

/** The A1 eligibility of the number. A robot user is never checked. */
function banInfo(u: User): Status {
  if (u.engine !== "a1") {
    return { label: "—", tone: "neutral", hint: "Roboter-Einspielungen werden nicht geprüft." };
  }
  switch (u.banCheck) {
    case "ok":
      return { label: "OK", tone: "success" };
    case "failed":
      return {
        label: "Fehler",
        tone: "danger",
        hint: u.banError || "Die Berechtigungsprüfung bei A1 schlug fehl.",
      };
    case "no_number":
      return {
        label: "keine Nr.",
        tone: "warning",
        hint: "Ohne Rufnummer kann A1 die Berechtigung nicht prüfen.",
      };
    default:
      return { label: "—", tone: "neutral" };
  }
}

/** Whether the recordings this person's announcements need are on disk. */
function filesInfo(u: User): Status {
  if (u.recording === undefined) return { label: "—", tone: "neutral" };

  const missingProxy = u.proxyRecording === false;
  const proxyHint = missingProxy
    ? " Die Aufnahmen, die diese Person als Vertretung nennen, fehlen."
    : "";

  switch (u.recording) {
    case "own":
      return missingProxy
        ? { label: "Vertr. fehlt", tone: "warning", hint: proxyHint.trim() }
        : { label: "OK", tone: "success", hint: "Alle Aufnahmen sind vorhanden." };
    case "generic":
      return {
        label: "allgemein",
        tone: "warning",
        hint:
          "Eigene Aufnahmen fehlen; die allgemeinen Bausteine springen ein." + proxyHint,
      };
    default:
      return {
        label: "fehlt",
        tone: "danger",
        hint: "Aufnahmen fehlen; eine Ansage käme ohne diesen Satz heraus." + proxyHint,
      };
  }
}

export function roleLabel(role: string): string {
  switch (role) {
    case "employee":
      return "Mitarbeiter";
    case "secretariat":
      return "Sekretariat";
    case "company_admin":
      return "Firmenadmin";
    case "self_admin":
      return "Mehrfirmen-Admin";
    case "global_admin":
      return "Globaler Admin";
    default:
      return role;
  }
}

/** "Nachname, Vorname" — without a dangling comma when one half is empty. */
function fullName(u: Pick<User, "firstName" | "lastName">): string {
  return [u.lastName, u.firstName].filter(Boolean).join(", ") || "—";
}

/**
 * What the new tab shows while the one-off link is being fetched.
 *
 * It is written into an about:blank window that still belongs to this origin,
 * which is the only moment at which anything can be put there: a fraction of a
 * second later it is on the portal's origin and out of reach.
 */
const PLACEHOLDER = `<!doctype html><html lang="de"><head><meta charset="utf-8">
<title>Portal wird geöffnet …</title><style>
body{margin:0;height:100vh;display:grid;place-items:center;background:#f8fafc;color:#475569;
     font:14px/1.5 ui-sans-serif,system-ui,sans-serif}
@media (prefers-color-scheme:dark){body{background:#0f172a;color:#94a3b8}}
</style></head><body>Portal wird geöffnet …</body></html>`;

/**
 * Why the portal cannot be opened for this person, or undefined when it can.
 *
 * Disabled and explained rather than hidden: a control that is missing on
 * scattered rows makes the column ragged and leaves the reader guessing
 * whether the feature is broken, where a greyed one with a reason teaches the
 * rule on the first hover. The server applies the same two rules.
 */
function portalReason(u: User): string | undefined {
  if (!u.active) return "Inaktive Benutzer können das Portal nicht benutzen";
  if (u.role === "secretariat") return "Ein Sekretariat hat keinen Portalzugang";
  return undefined;
}

/** The three refusals the endpoint can send, in words an administrator can act on. */
function impersonateError(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.status) {
      case 403:
        return "Sie dürfen das Portal nicht im Namen anderer Personen öffnen.";
      case 409:
        return "Diese Person kann das Portal nicht benutzen — inaktiv oder Sekretariat.";
      case 412:
        return "Für dieses System ist keine Portal-Adresse hinterlegt.";
      default:
        return e.message || "Das Portal konnte nicht geöffnet werden.";
    }
  }
  return (e as Error).message;
}

/** Kept for the pages that show a person's name without loading the record. */
export function useUserName(users: User[] | undefined) {
  return useMemo(() => {
    const index = new Map<number, string>();
    for (const u of users ?? []) index.set(u.id, `${u.lastName}, ${u.firstName}`);
    return index;
  }, [users]);
}


/**
 * How the name is said, and a way to hear it.
 *
 * Only relevant once a company's announcements are spoken, but harmless
 * before that, so it is not hidden behind the mode: an administrator can
 * prepare the difficult names before switching anything over.
 *
 * The sample is always a whole sentence. A name on its own gets its own
 * closing intonation and sounds wrong however it is pronounced, which would
 * send people chasing a problem that is not there.
 */
function SpokenNameField({
  companyId,
  value,
  fallback,
  onChange,
}: {
  companyId: number;
  value: string;
  fallback: string;
  onChange: (v: string) => void;
}) {
  const [src, setSrc] = useState("");
  const spoken = value.trim() || fallback;

  return (
    <Field
      label="Aussprache"
      hint="Nur ausfüllen, wenn die Schreibweise nicht sagt, wie der Name klingt. Ganz normal deutsch geschrieben."
    >
      <div className="flex gap-2">
        <Input
          value={value}
          placeholder={fallback}
          onChange={(e) => onChange(e.target.value)}
        />
        <Button
          type="button"
          disabled={!companyId || !spoken}
          onClick={() =>
            setSrc(
              `/api/admin/companies/${companyId}/speech/pronounce?name=${encodeURIComponent(spoken)}&t=${Date.now()}`,
            )
          }
        >
          Anhören
        </Button>
      </div>
      {src && (
        <div className="mt-2">
          <AudioPlayer src={src} />
        </div>
      )}
    </Field>
  );
}
